import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { DfeSyncStatus, InboundDfeList, InboundDfeTab } from "@cx/contracts";
import { formatBRL, formatDateBR, formatDocument, todayInSaoPaulo } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { dateTimeBR } from "../invoices/InvoiceEvents";
import {
  IMPORT_LABEL,
  IMPORT_TONE,
  MANIFEST_LABEL,
  MANIFEST_TONE,
  SEFAZ_LABEL,
  SEFAZ_TONE,
  inboundTitle,
} from "./labels";

const TABS: { key: InboundDfeTab; label: string }[] = [
  { key: "received", label: "Notas recebidas" },
  { key: "purchases", label: "Notas de compra" },
];

/**
 * Suprimentos › Notas de entrada (T2.5, RF-SUP-04): notas recebidas pela distribuição da SEFAZ (DistDFe) ou pelo envio
 * do XML; "Notas de compra" são as que já tiveram a entrada lançada. Buscar na SEFAZ, configuração da busca
 * automática e da ciência automática, e envio de XML.
 */
export function InboundListPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const canUpdate = can(perms, "inbound_invoices", "update");
  const canCreate = can(perms, "inbound_invoices", "create");
  const [tab, setTab] = useState<InboundDfeTab>("received");
  const [q, setQ] = useState("");
  const term = q.trim();
  const list = useInfiniteQuery({
    queryKey: ["inbound-dfe", companyId, "list", tab, term],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<InboundDfeList>(
        `/inbound-dfe?${new URLSearchParams({
          tab,
          ...(term ? { q: term } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: (query) =>
      query.state.data?.pages.some((p) => p.data.some((d) => d.manifestPending)) ? 3000 : false,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const summary = list.data?.pages[0]?.summary;
  return (
    <>
      <ContentHeader title="Notas de entrada" breadcrumb={["Suprimentos", "Notas de entrada"]} />
      <SyncPanel canUpdate={canUpdate} canCreate={canCreate} />
      <div className="box card">
        <ul className="nav nav-tabs px-3 pt-3" role="tablist">
          {TABS.map((t) => (
            <li className="nav-item" role="presentation" key={t.key}>
              <button
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={`nav-link${tab === t.key ? " active" : ""}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            </li>
          ))}
        </ul>
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-6">
            <label className="visually-hidden" htmlFor="inbound-q">
              Pesquisar nota de entrada
            </label>
            <input
              id="inbound-q"
              type="search"
              className="form-control"
              placeholder="Pesquisar por número, chave ou fornecedor"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title={tab === "received" ? "Nenhuma nota recebida" : "Nenhuma nota de compra"}
            description={
              tab === "received"
                ? "Busque as notas emitidas contra o CNPJ da empresa na SEFAZ ou envie o XML recebido do fornecedor."
                : "Lance a entrada de uma nota recebida (estoque e contas a pagar) para ela aparecer aqui."
            }
            actions={[]}
          />
        )}
        {rows.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Notas de entrada">
                <thead>
                  <tr>
                    <th scope="col">Número</th>
                    <th scope="col">Emissão</th>
                    <th scope="col">Fornecedor</th>
                    <th scope="col" className="text-end">
                      Valor
                    </th>
                    <th scope="col">Manifestação</th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="text-nowrap">
                        <Link to="/suprimentos/notas-de-entrada/$id" params={{ id: r.id }}>
                          {inboundTitle(r)}
                        </Link>
                        {r.environment === 2 && (
                          <span className="badge text-bg-warning ms-2" title="Sem valor fiscal">
                            Homologação
                          </span>
                        )}
                        {r.xmlStatus === "summary" && <span className="text-muted small ms-2">resumo</span>}
                      </td>
                      <td>{r.issuedAt ? formatDateBR(todayInSaoPaulo(new Date(r.issuedAt))) : "—"}</td>
                      <td>
                        {r.issuerName ?? "—"}
                        {r.issuerDocument && (
                          <span className="text-muted small ms-2">{formatDocument(r.issuerDocument)}</span>
                        )}
                      </td>
                      <td className="text-end">{formatBRL(r.totalCents)}</td>
                      <td>
                        {r.manifestPending ? (
                          <StatusBadge tone="info">Enviando: {MANIFEST_LABEL[r.manifestPending]}</StatusBadge>
                        ) : r.manifestStatus ? (
                          <StatusBadge tone={MANIFEST_TONE[r.manifestStatus]}>
                            {MANIFEST_LABEL[r.manifestStatus]}
                          </StatusBadge>
                        ) : (
                          <span className="text-muted">Sem manifestação</span>
                        )}
                      </td>
                      <td className="text-nowrap">
                        {r.sefazStatus !== "authorized" ? (
                          <StatusBadge tone={SEFAZ_TONE[r.sefazStatus]}>
                            {SEFAZ_LABEL[r.sefazStatus]}
                          </StatusBadge>
                        ) : (
                          <StatusBadge tone={IMPORT_TONE[r.importStatus]}>
                            {IMPORT_LABEL[r.importStatus]}
                          </StatusBadge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3">
              {summary && (
                <span className="text-muted small" data-testid="inbound-summary">
                  {summary.count} {summary.count === 1 ? "nota" : "notas"} · {formatBRL(summary.totalCents)}
                </span>
              )}
              {list.hasNextPage && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

/** Busca na SEFAZ (DistDFe), situação da última consulta, configuração e envio de XML. */
function SyncPanel({ canUpdate, canCreate }: { canUpdate: boolean; canCreate: boolean }): ReactElement {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const key = ["inbound-dfe", companyId, "sync-status"];
  const status = useQuery({
    queryKey: key,
    queryFn: () => api<DfeSyncStatus>("/inbound-dfe/sync-status"),
    refetchInterval: (query) => (query.state.data && query.state.data.status !== "idle" ? 2500 : false),
  });
  const s = status.data;
  const refresh = () => qc.invalidateQueries({ queryKey: ["inbound-dfe", companyId] });
  const sync = useMutation({
    mutationFn: () => api("/inbound-dfe/sync", { method: "POST" }),
    onSuccess: () => {
      toast("info", "Busca na SEFAZ iniciada");
      void refresh();
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const settings = useMutation({
    mutationFn: (v: { autoSync: boolean; autoCiencia: boolean }) =>
      api<DfeSyncStatus>("/inbound-dfe/settings", { method: "PUT", body: JSON.stringify(v) }),
    onSuccess: (v) => {
      qc.setQueryData(key, v);
      toast("success", "Configuração salva");
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const upload = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return api<{ id: string; created: boolean }>("/inbound-dfe/upload", { method: "POST", body: fd });
    },
    onSuccess: (r) => {
      toast("success", r.created ? "XML importado" : "Nota já recebida: XML completo gravado");
      void refresh();
      void navigate({ to: "/suprimentos/notas-de-entrada/$id", params: { id: r.id } });
    },
    onError: (e) => toast("danger", errorMessage(e)),
    onSettled: () => {
      if (fileRef.current) fileRef.current.value = "";
    },
  });
  const busy = !!s && s.status !== "idle";
  // Terminou uma busca (a automática também): a lista traz as notas novas.
  const lastRun = s?.lastRunAt ?? null;
  useEffect(() => {
    if (lastRun) void qc.invalidateQueries({ queryKey: ["inbound-dfe", companyId, "list"] });
  }, [lastRun, qc, companyId]);
  return (
    <div className="box card mb-3">
      <div className="card-body d-flex flex-wrap align-items-start gap-3">
        <div className="flex-grow-1" data-testid="dfe-sync-status">
          <div className="fw-semibold">Notas emitidas contra a empresa (SEFAZ)</div>
          {status.isLoading && <div className="text-muted small">Carregando…</div>}
          {s && (
            <div className="small text-muted">
              {s.cnpj ? (
                <>CNPJ {formatDocument(s.cnpj)}</>
              ) : (
                <>Informe o CNPJ da empresa para buscar notas.</>
              )}
              {s.provider === "sandbox" && (
                <span className="badge text-bg-warning ms-2">Provedor de testes</span>
              )}
              <br />
              {busy
                ? "Buscando notas na SEFAZ…"
                : s.lastRunAt
                  ? `Última consulta: ${dateTimeBR(s.lastRunAt)}`
                  : "Nenhuma consulta feita ainda."}
              {s.lastMessage && !busy && <> · {s.lastMessage}</>}
              {s.nextAllowedAt && !busy && Date.parse(s.nextAllowedAt) > Date.now() && (
                <> · Próxima consulta permitida: {dateTimeBR(s.nextAllowedAt)}</>
              )}
            </div>
          )}
          {s && (
            <div className="d-flex flex-wrap gap-3 mt-2">
              <div className="form-check form-switch">
                <input
                  id="dfe-auto-sync"
                  type="checkbox"
                  role="switch"
                  className="form-check-input"
                  checked={s.autoSync}
                  disabled={!canUpdate || settings.isPending}
                  onChange={(e) =>
                    settings.mutate({ autoSync: e.target.checked, autoCiencia: s.autoCiencia })
                  }
                />
                <label className="form-check-label small" htmlFor="dfe-auto-sync">
                  Buscar automaticamente
                </label>
              </div>
              <div className="form-check form-switch">
                <input
                  id="dfe-auto-ciencia"
                  type="checkbox"
                  role="switch"
                  className="form-check-input"
                  checked={s.autoCiencia}
                  disabled={!canUpdate || settings.isPending}
                  onChange={(e) => settings.mutate({ autoSync: s.autoSync, autoCiencia: e.target.checked })}
                />
                <label className="form-check-label small" htmlFor="dfe-auto-ciencia">
                  Dar ciência automaticamente (para receber o XML completo)
                </label>
              </div>
            </div>
          )}
        </div>
        <div className="d-flex flex-wrap gap-2">
          {canUpdate && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={sync.isPending || busy || !s?.cnpj}
              onClick={() => sync.mutate()}
            >
              {busy ? "Buscando…" : "Buscar notas na SEFAZ"}
            </button>
          )}
          {canCreate && (
            <>
              <label className="visually-hidden" htmlFor="inbound-xml">
                Arquivo XML da NF-e
              </label>
              <input
                id="inbound-xml"
                ref={fileRef}
                type="file"
                accept=".xml,application/xml,text/xml"
                className="d-none"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) upload.mutate(f);
                }}
              />
              <button
                type="button"
                className="btn btn-outline-primary"
                disabled={upload.isPending}
                onClick={() => fileRef.current?.click()}
              >
                {upload.isPending ? "Enviando…" : "Enviar XML"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
